import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { AppRootState, Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  ComplianceItemUserJoin,
  ComplianceProgress,
  ComplianceRequirement,
  ComplianceRequirementDetail,
  ComplianceUserItemJoin,
  RecertCycleDetail,
  RecertificationCycleTemplate,
  RecertificationCycleTemplateDetail,
} from 'src/v3/core/types'
import { CreateCompliancePayload } from './compliance/types'
import { fetchAllPages } from './manage.slice'

// ─── Local types ─────────────────────────────────────────────────────────────

// ─── State ───────────────────────────────────────────────────────────────────

type ManageComplianceState = {
  complianceRequirements: Record<string, ComplianceRequirement[]>
  complianceRequirementsStatus: Record<string, Status>
  complianceRequirementDetail: Record<string, ComplianceRequirementDetail>
  complianceRequirementDetailStatus: Record<string, Status>
  complianceProgress: Record<string, ComplianceProgress>
  complianceProgressStatus: Record<string, Status>
  complianceItemUsers: Record<string, ComplianceItemUserJoin[]>
  complianceItemUsersStatus: Record<string, Status>
  complianceUserItems: Record<string, ComplianceUserItemJoin[]>
  complianceUserItemsStatus: Record<string, Status>
  recertCycleDetail: Record<string, RecertCycleDetail>
  recertCycleDetailStatus: Record<string, Status>
  recertCycleTemplates: Record<string, RecertificationCycleTemplate[]>
  recertCycleTemplatesStatus: Record<string, Status>
  recertCycleTemplateDetail: Record<string, RecertificationCycleTemplateDetail>
  recertCycleTemplateDetailStatus: Record<string, Status>
}

export let initialState: ManageComplianceState = {
  complianceRequirements: {},
  complianceRequirementsStatus: {},
  complianceRequirementDetail: {},
  complianceRequirementDetailStatus: {},
  complianceProgress: {},
  complianceProgressStatus: {},
  complianceItemUsers: {},
  complianceItemUsersStatus: {},
  complianceUserItems: {},
  complianceUserItemsStatus: {},
  recertCycleDetail: {},
  recertCycleDetailStatus: {},
  recertCycleTemplates: {},
  recertCycleTemplatesStatus: {},
  recertCycleTemplateDetail: {},
  recertCycleTemplateDetailStatus: {},
}

// ─── Thunks ───────────────────────────────────────────────────────────────────

export let createComplianceRequirement = createAsyncThunk(
  'manageCompliance/createComplianceRequirement',
  (payload: CreateCompliancePayload) =>
    axios
      .post<{ id: string }>('/api/manage/compliance', payload)
      .then(pluckData),
)

export let fetchComplianceRequirements = createAsyncThunk(
  'manageCompliance/fetchComplianceRequirements',
  (linkedServiceID: string) =>
    fetchAllPages<ComplianceRequirement>('/api/manage/compliance', 500, {
      linkedServiceID,
    }),
)

export let fetchComplianceRequirementDetail = createAsyncThunk(
  'manageCompliance/fetchComplianceRequirementDetail',
  (id: string) =>
    axios
      .get<ComplianceRequirementDetail>(`/api/manage/compliance/${id}`)
      .then(pluckData),
)

export let fetchComplianceProgress = createAsyncThunk(
  'manageCompliance/fetchComplianceProgress',
  (id: string) =>
    axios
      .get<ComplianceProgress>(`/api/manage/compliance/${id}/progress`)
      .then(pluckData),
)

export let fetchComplianceItemUsers = createAsyncThunk(
  'manageCompliance/fetchComplianceItemUsers',
  ({ complianceID, itemID }: { complianceID: string; itemID: string }) =>
    axios
      .get<ComplianceItemUserJoin[]>(
        `/api/manage/compliance/${complianceID}/items/${itemID}/users`,
      )
      .then(pluckData),
)

export let fetchComplianceUserItems = createAsyncThunk(
  'manageCompliance/fetchComplianceUserItems',
  ({ complianceID, userID }: { complianceID: string; userID: string }) =>
    axios
      .get<ComplianceUserItemJoin[]>(
        `/api/manage/compliance/${complianceID}/users/${userID}/items`,
      )
      .then(pluckData),
)

export let fetchRecertCycleTemplateDetail = createAsyncThunk(
  'manageCompliance/fetchRecertCycleTemplateDetail',
  (templateID: string) =>
    axios
      .get<RecertificationCycleTemplateDetail>(
        `/api/manage/recertification-cycle/template/${templateID}`,
      )
      .then(pluckData),
)

export let fetchRecertCycleTemplates = createAsyncThunk(
  'manageCompliance/fetchRecertCycleTemplates',
  (linkedServiceID: string) =>
    axios
      .get<RecertificationCycleTemplate[]>(
        `/api/manage/recertification-cycle/templates/${linkedServiceID}`,
      )
      .then(pluckData),
)

export let fetchRecertCycleDetail = createAsyncThunk(
  'manageCompliance/fetchRecertCycleDetail',
  (cycleID: string) =>
    axios
      .get<RecertCycleDetail>(
        `/api/manage/compliance/recert-cycle/${cycleID}/detail`,
      )
      .then(pluckData),
)

// ─── Slice ────────────────────────────────────────────────────────────────────

let manageComplianceSlice = createSlice({
  name: 'manageCompliance',
  initialState,
  reducers: {
    clearComplianceUserData: (state) => {
      state.complianceUserItems = {}
      state.complianceUserItemsStatus = {}
      state.complianceItemUsers = {}
      state.complianceItemUsersStatus = {}
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchComplianceRequirements.pending, (state, action) => {
        state.complianceRequirementsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchComplianceRequirements.rejected, (state, action) => {
        state.complianceRequirementsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchComplianceRequirements.fulfilled, (state, action) => {
        state.complianceRequirementsStatus[action.meta.arg] = 'fulfilled'
        state.complianceRequirements[action.meta.arg] = action.payload
      })
      .addCase(fetchComplianceRequirementDetail.pending, (state, action) => {
        state.complianceRequirementDetailStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchComplianceRequirementDetail.rejected, (state, action) => {
        state.complianceRequirementDetailStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchComplianceRequirementDetail.fulfilled, (state, action) => {
        state.complianceRequirementDetailStatus[action.meta.arg] = 'fulfilled'
        state.complianceRequirementDetail[action.meta.arg] = action.payload
      })
      .addCase(fetchComplianceProgress.pending, (state, action) => {
        state.complianceProgressStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchComplianceProgress.rejected, (state, action) => {
        state.complianceProgressStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchComplianceProgress.fulfilled, (state, action) => {
        state.complianceProgressStatus[action.meta.arg] = 'fulfilled'
        state.complianceProgress[action.meta.arg] = action.payload
      })
      .addCase(fetchComplianceItemUsers.pending, (state, action) => {
        let key = `${action.meta.arg.complianceID}:${action.meta.arg.itemID}`
        state.complianceItemUsersStatus[key] = 'pending'
      })
      .addCase(fetchComplianceItemUsers.rejected, (state, action) => {
        let key = `${action.meta.arg.complianceID}:${action.meta.arg.itemID}`
        state.complianceItemUsersStatus[key] = 'rejected'
      })
      .addCase(fetchComplianceItemUsers.fulfilled, (state, action) => {
        let key = `${action.meta.arg.complianceID}:${action.meta.arg.itemID}`
        state.complianceItemUsersStatus[key] = 'fulfilled'
        state.complianceItemUsers[key] = action.payload
      })
      .addCase(fetchComplianceUserItems.pending, (state, action) => {
        let key = `${action.meta.arg.complianceID}:${action.meta.arg.userID}`
        state.complianceUserItemsStatus[key] = 'pending'
      })
      .addCase(fetchComplianceUserItems.rejected, (state, action) => {
        let key = `${action.meta.arg.complianceID}:${action.meta.arg.userID}`
        state.complianceUserItemsStatus[key] = 'rejected'
      })
      .addCase(fetchComplianceUserItems.fulfilled, (state, action) => {
        let key = `${action.meta.arg.complianceID}:${action.meta.arg.userID}`
        state.complianceUserItemsStatus[key] = 'fulfilled'
        state.complianceUserItems[key] = action.payload
      })
      .addCase(fetchRecertCycleDetail.pending, (state, action) => {
        state.recertCycleDetailStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchRecertCycleDetail.rejected, (state, action) => {
        state.recertCycleDetailStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchRecertCycleDetail.fulfilled, (state, action) => {
        state.recertCycleDetailStatus[action.meta.arg] = 'fulfilled'
        state.recertCycleDetail[action.meta.arg] = action.payload
      })
      .addCase(fetchRecertCycleTemplateDetail.pending, (state, action) => {
        state.recertCycleTemplateDetailStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchRecertCycleTemplateDetail.rejected, (state, action) => {
        state.recertCycleTemplateDetailStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchRecertCycleTemplateDetail.fulfilled, (state, action) => {
        state.recertCycleTemplateDetailStatus[action.meta.arg] = 'fulfilled'
        state.recertCycleTemplateDetail[action.meta.arg] = action.payload
      })
      .addCase(fetchRecertCycleTemplates.pending, (state, action) => {
        state.recertCycleTemplatesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchRecertCycleTemplates.rejected, (state, action) => {
        state.recertCycleTemplatesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchRecertCycleTemplates.fulfilled, (state, action) => {
        state.recertCycleTemplatesStatus[action.meta.arg] = 'fulfilled'
        state.recertCycleTemplates[action.meta.arg] = action.payload
      })
  },
})

export let { clearComplianceUserData } = manageComplianceSlice.actions

// ─── Selectors ────────────────────────────────────────────────────────────────

export let selectComplianceRequirementsByLSID =
  (id: string) => (state: AppRootState) =>
    state.manageCompliance.complianceRequirements[id]
export let selectComplianceRequirementsStatusByLSID =
  (id: string) => (state: AppRootState) =>
    state.manageCompliance.complianceRequirementsStatus[id]
export let selectComplianceRequirementDetail =
  (id: string) => (state: AppRootState) =>
    state.manageCompliance.complianceRequirementDetail[id]
export let selectComplianceRequirementDetailStatus =
  (id: string) => (state: AppRootState) =>
    state.manageCompliance.complianceRequirementDetailStatus[id]
export let selectComplianceProgress = (id: string) => (state: AppRootState) =>
  state.manageCompliance.complianceProgress[id]
export let selectComplianceProgressStatus =
  (id: string) => (state: AppRootState) =>
    state.manageCompliance.complianceProgressStatus[id]
export let selectComplianceItemUsers =
  (complianceID: string, itemID: string) => (state: AppRootState) =>
    state.manageCompliance.complianceItemUsers[`${complianceID}:${itemID}`]
export let selectComplianceItemUsersStatus =
  (complianceID: string, itemID: string) => (state: AppRootState) =>
    state.manageCompliance.complianceItemUsersStatus[
      `${complianceID}:${itemID}`
    ]
export let selectComplianceUserItems =
  (complianceID: string, userID: string) => (state: AppRootState) =>
    state.manageCompliance.complianceUserItems[`${complianceID}:${userID}`]
export let selectComplianceUserItemsStatus =
  (complianceID: string, userID: string) => (state: AppRootState) =>
    state.manageCompliance.complianceUserItemsStatus[
      `${complianceID}:${userID}`
    ]
export let selectRecertCycleDetail =
  (cycleID: string) => (state: AppRootState) =>
    state.manageCompliance.recertCycleDetail[cycleID]
export let selectRecertCycleDetailStatus =
  (cycleID: string) => (state: AppRootState) =>
    state.manageCompliance.recertCycleDetailStatus[cycleID]
export let selectRecertCycleTemplateDetail =
  (templateID: string) => (state: AppRootState) =>
    state.manageCompliance.recertCycleTemplateDetail[templateID]
export let selectRecertCycleTemplateDetailStatus =
  (templateID: string) => (state: AppRootState) =>
    state.manageCompliance.recertCycleTemplateDetailStatus[templateID]
export let selectRecertCycleTemplates =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manageCompliance.recertCycleTemplates[linkedServiceID]
export let selectRecertCycleTemplatesStatus =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manageCompliance.recertCycleTemplatesStatus[linkedServiceID]

export default manageComplianceSlice.reducer
